import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import { Toaster, toast } from "sonner";
import { 
  Settings, 
  Download, 
  Plus, 
  Minus, 
  RotateCcw, 
  Upload, 
  Trash2, 
  Edit2, 
  Check, 
  X,
  FileText,
  Layers
} from "lucide-react";

import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Slider } from "./components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "./components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./components/ui/popover";
import { Textarea } from "./components/ui/textarea";
import { ScrollArea } from "./components/ui/scroll-area";

const configuredBackendUrl = (process.env.REACT_APP_BACKEND_URL || "").trim();
const API = configuredBackendUrl
  ? `${configuredBackendUrl.replace(/\/$/, "")}/api`
  : "/api";

// ============ FILE CARD COMPONENT ============
const FileCard = ({ 
  file, 
  index, 
  onRemove, 
  onUpdate,
  cutPresets,
  holePresets,
  onOpenPresets
}) => {
  const [zoom, setZoom] = useState(100);
  const [quantity, setQuantity] = useState(1);
  const [cutLength, setCutLength] = useState(file.cut_length_meters);
  const [cutLengthModified, setCutLengthModified] = useState(false);
  const [cutPrice, setCutPrice] = useState(100);
  const [holePrices, setHolePrices] = useState({});
  const [sampling, setSampling] = useState(0); // Выборка

  // Initialize hole prices
  useEffect(() => {
    const prices = {};
    file.holes.forEach(hole => {
      prices[hole.diameter_mm] = 20;
    });
    setHolePrices(prices);
  }, [file.holes]);

  const handleCutLengthChange = (value) => {
    setCutLength(parseFloat(value) || 0);
    setCutLengthModified(true);
  };

  const resetCutLength = () => {
    setCutLength(file.calculated_cut_length);
    setCutLengthModified(false);
  };

  const selectCutPreset = (preset) => {
    setCutPrice(preset.price);
  };

  const selectHolePreset = (diameter, preset) => {
    setHolePrices(prev => ({ ...prev, [diameter]: preset.price }));
  };

  // Calculate totals
  const cutTotal = Math.round((cutLength * cutPrice * quantity) / 10) * 10;
  const holesTotal = file.holes.reduce((sum, hole) => {
    const price = holePrices[hole.diameter_mm] || 20;
    return sum + Math.round((hole.count * price * quantity) / 10) * 10;
  }, 0);
  const samplingTotal = Math.round(sampling * quantity / 10) * 10;
  const fileTotal = cutTotal + holesTotal + samplingTotal;

  // Update parent with totals
  useEffect(() => {
    onUpdate(index, {
      cutLength,
      cutPrice,
      cutTotal,
      holePrices,
      holesTotal,
      sampling,
      samplingTotal,
      fileTotal,
      quantity
    });
  }, [cutLength, cutPrice, cutTotal, holePrices, holesTotal, sampling, samplingTotal, fileTotal, quantity, index, onUpdate]);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-sm animate-slide-in" data-testid={`file-card-${index}`}>
      <div className="flex flex-col lg:flex-row">
        {/* Preview Section */}
        <div className="lg:w-[600px] flex-shrink-0 p-4 border-b lg:border-b-0 lg:border-r border-slate-800">
          {/* File header */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-manrope font-bold text-white truncate max-w-[400px]" title={file.filename}>
                {file.filename}
              </h3>
              <p className="text-xs text-slate-500 font-mono">
                {file.width_mm.toFixed(1)} × {file.height_mm.toFixed(1)} мм
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onRemove(index)}
              className="text-slate-500 hover:text-red-500 hover:bg-red-500/10"
              data-testid={`remove-file-${index}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          {/* SVG Preview */}
          <div 
            className="preview-container rounded-sm relative overflow-auto"
            style={{ width: '100%', height: '360px' }}
          >
            <div
              style={{
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'center center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100%',
                minWidth: '100%'
              }}
              dangerouslySetInnerHTML={{ __html: file.svg_data }}
            />
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center gap-3 mt-3">
            <Button
              variant="secondary"
              size="icon"
              className="h-8 w-8"
              onClick={() => setZoom(Math.max(10, zoom - 10))}
              data-testid={`zoom-out-${index}`}
            >
              <Minus className="w-4 h-4" />
            </Button>
            
            <Slider
              value={[zoom]}
              min={10}
              max={500}
              step={10}
              onValueChange={(value) => setZoom(value[0])}
              className="flex-1"
              data-testid={`zoom-slider-${index}`}
            />
            
            <Button
              variant="secondary"
              size="icon"
              className="h-8 w-8"
              onClick={() => setZoom(Math.min(500, zoom + 10))}
              data-testid={`zoom-in-${index}`}
            >
              <Plus className="w-4 h-4" />
            </Button>
            
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setZoom(100)}
              className="font-mono text-xs"
              data-testid={`zoom-reset-${index}`}
            >
              {zoom}%
            </Button>
          </div>
        </div>

        {/* Calculation Section */}
        <div className="flex-1 p-4">
          {/* Quantity */}
          <div className="flex items-center gap-3 mb-4">
            <Label className="text-xs font-manrope uppercase tracking-widest text-slate-500 w-20">
              Тираж
            </Label>
            <Input
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-24 bg-slate-950 border-slate-800 font-mono text-amber-500"
              data-testid={`quantity-${index}`}
            />
            <span className="text-slate-500 text-sm">шт.</span>
          </div>

          {/* Cut Length */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Label className="text-xs font-manrope uppercase tracking-widest text-slate-500 w-20">
              Рез
            </Label>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                step={0.1}
                value={cutLength}
                onChange={(e) => handleCutLengthChange(e.target.value)}
                className="w-20 bg-slate-950 border-slate-800 font-mono text-white"
                data-testid={`cut-length-${index}`}
              />
              <span className="text-slate-500 text-sm">м</span>
              {cutLengthModified && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-slate-500 hover:text-white"
                  onClick={resetCutLength}
                  data-testid={`reset-cut-${index}`}
                >
                  <RotateCcw className="w-3 h-3" />
                </Button>
              )}
            </div>
            <span className="text-slate-600">×</span>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                value={cutPrice}
                onChange={(e) => setCutPrice(parseFloat(e.target.value) || 0)}
                className="w-20 bg-slate-950 border-slate-800 font-mono text-white"
                data-testid={`cut-price-${index}`}
              />
              <span className="text-slate-500 text-sm">₽/м</span>
            </div>
            <span className="text-slate-600">=</span>
            <span className="font-mono text-amber-500 font-bold">{cutTotal} ₽</span>
            
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  className="ml-2"
                  data-testid={`cut-preset-btn-${index}`}
                >
                  <Layers className="w-3 h-3 mr-1" />
                  Материал
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 bg-slate-900 border-slate-700">
                <div className="space-y-2">
                  <h4 className="font-manrope font-bold text-sm text-white">Пресеты реза</h4>
                  <ScrollArea className="h-40">
                    {cutPresets.map(preset => (
                      <button
                        key={preset.id}
                        onClick={() => selectCutPreset(preset)}
                        className="w-full flex items-center justify-between p-2 hover:bg-slate-800 rounded text-left"
                        data-testid={`cut-preset-${preset.id}`}
                      >
                        <span className="text-slate-300">{preset.name}</span>
                        <span className="font-mono text-amber-500">{preset.price} ₽/м</span>
                      </button>
                    ))}
                  </ScrollArea>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2"
                    onClick={() => onOpenPresets('cut')}
                    data-testid="manage-cut-presets"
                  >
                    <Settings className="w-3 h-3 mr-1" />
                    Управление пресетами
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Holes */}
          {file.holes.length > 0 && (
            <div className="space-y-2 mb-4">
              <Label className="text-xs font-manrope uppercase tracking-widest text-slate-500">
                Отверстия
              </Label>
              {file.holes.map((hole, holeIdx) => (
                <div key={holeIdx} className="flex items-center gap-2 flex-wrap">
                  <span className="text-slate-400 text-sm w-16">⌀{hole.diameter_mm}мм:</span>
                  <span className="font-mono text-white">{hole.count * quantity}</span>
                  <span className="text-slate-600">×</span>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      value={holePrices[hole.diameter_mm] || 20}
                      onChange={(e) => setHolePrices(prev => ({
                        ...prev,
                        [hole.diameter_mm]: parseFloat(e.target.value) || 0
                      }))}
                      className="w-16 bg-slate-950 border-slate-800 font-mono text-white text-sm"
                      data-testid={`hole-price-${index}-${holeIdx}`}
                    />
                    <span className="text-slate-500 text-sm">₽/шт</span>
                  </div>
                  <span className="text-slate-600">=</span>
                  <span className="font-mono text-green-500">
                    {Math.round((hole.count * (holePrices[hole.diameter_mm] || 20) * quantity) / 10) * 10} ₽
                  </span>
                  
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-6 px-2">
                        <Layers className="w-3 h-3" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 bg-slate-900 border-slate-700">
                      <div className="space-y-2">
                        <h4 className="font-manrope font-bold text-sm text-white">Пресеты отверстий</h4>
                        <ScrollArea className="h-32">
                          {holePresets.map(preset => (
                            <button
                              key={preset.id}
                              onClick={() => selectHolePreset(hole.diameter_mm, preset)}
                              className="w-full flex items-center justify-between p-2 hover:bg-slate-800 rounded text-left"
                            >
                              <span className="text-slate-300">{preset.name}</span>
                              <span className="font-mono text-green-500">{preset.price} ₽</span>
                            </button>
                          ))}
                        </ScrollArea>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              ))}
            </div>
          )}

          {/* Sampling / Выборка */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Label className="text-xs font-manrope uppercase tracking-widest text-slate-500 w-20">
              Выборка
            </Label>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                value={sampling}
                onChange={(e) => setSampling(parseFloat(e.target.value) || 0)}
                className="w-24 bg-slate-950 border-slate-800 font-mono text-white"
                data-testid={`sampling-${index}`}
              />
              <span className="text-slate-500 text-sm">₽</span>
            </div>
            {quantity > 1 && sampling > 0 && (
              <>
                <span className="text-slate-600">×</span>
                <span className="text-slate-400">{quantity}</span>
                <span className="text-slate-600">=</span>
                <span className="font-mono text-cyan-500">{samplingTotal} ₽</span>
              </>
            )}
          </div>

          {/* File Total */}
          <div className="flex items-center justify-between pt-3 border-t border-slate-800">
            <span className="font-manrope font-bold text-slate-400">Итого по файлу:</span>
            <span className="font-mono text-xl text-amber-500 font-bold" data-testid={`file-total-${index}`}>
              {fileTotal.toLocaleString('ru-RU')} ₽
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ COMPANY SETTINGS DIALOG ============
const CompanySettingsDialog = ({ open, onOpenChange, companyDetails, onSave }) => {
  const [details, setDetails] = useState(companyDetails);

  useEffect(() => {
    setDetails(companyDetails);
  }, [companyDetails]);

  const handleSave = () => {
    onSave(details);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-manrope text-white">Реквизиты компании</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label className="text-slate-400">Название компании</Label>
            <Input
              value={details.company_name}
              onChange={(e) => setDetails(prev => ({ ...prev, company_name: e.target.value }))}
              className="bg-slate-950 border-slate-800"
              data-testid="company-name-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-400">ИНН</Label>
              <Input
                value={details.inn}
                onChange={(e) => setDetails(prev => ({ ...prev, inn: e.target.value }))}
                className="bg-slate-950 border-slate-800"
                data-testid="company-inn-input"
              />
            </div>
            <div>
              <Label className="text-slate-400">Телефон</Label>
              <Input
                value={details.phone}
                onChange={(e) => setDetails(prev => ({ ...prev, phone: e.target.value }))}
                className="bg-slate-950 border-slate-800"
                data-testid="company-phone-input"
              />
            </div>
          </div>
          
          <div>
            <Label className="text-slate-400">Адрес</Label>
            <Input
              value={details.address}
              onChange={(e) => setDetails(prev => ({ ...prev, address: e.target.value }))}
              className="bg-slate-950 border-slate-800"
              data-testid="company-address-input"
            />
          </div>
          
          <div>
            <Label className="text-slate-400">Email</Label>
            <Input
              value={details.email}
              onChange={(e) => setDetails(prev => ({ ...prev, email: e.target.value }))}
              className="bg-slate-950 border-slate-800"
              data-testid="company-email-input"
            />
          </div>
          
          <div>
            <Label className="text-slate-400">Банковские реквизиты</Label>
            <Textarea
              value={details.bank_details}
              onChange={(e) => setDetails(prev => ({ ...prev, bank_details: e.target.value }))}
              className="bg-slate-950 border-slate-800 min-h-[80px]"
              data-testid="company-bank-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-400">НДС (%)</Label>
              <Input
                type="number"
                value={details.vat_rate}
                onChange={(e) => setDetails(prev => ({ ...prev, vat_rate: parseFloat(e.target.value) || 0 }))}
                className="bg-slate-950 border-slate-800"
                data-testid="company-vat-input"
              />
            </div>
            <div>
              <Label className="text-slate-400">Руководитель</Label>
              <Input
                value={details.director_name}
                onChange={(e) => setDetails(prev => ({ ...prev, director_name: e.target.value }))}
                className="bg-slate-950 border-slate-800"
                data-testid="company-director-input"
              />
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="save-company-btn">
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ PRESETS MANAGEMENT DIALOG ============
const PresetsDialog = ({ open, onOpenChange, presets, onSave, onDelete, type }) => {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', price: 0, diameter_mm: null });
  const [newPreset, setNewPreset] = useState({ name: '', price: 0, diameter_mm: null });
  const [showAddForm, setShowAddForm] = useState(false);

  const filteredPresets = presets.filter(p => p.type === type);

  const startEdit = (preset) => {
    setEditingId(preset.id);
    setEditForm({ name: preset.name, price: preset.price, diameter_mm: preset.diameter_mm });
  };

  const saveEdit = async () => {
    await onSave(editingId, { ...editForm, type });
    setEditingId(null);
  };

  const addPreset = async () => {
    await onSave(null, { ...newPreset, type });
    setNewPreset({ name: '', price: 0, diameter_mm: null });
    setShowAddForm(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-manrope text-white">
            {type === 'cut' ? 'Пресеты реза' : 'Пресеты отверстий'}
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[400px]">
          <div className="space-y-2 py-4">
            {filteredPresets.map(preset => (
              <div key={preset.id} className="flex items-center gap-2 p-2 bg-slate-950 rounded">
                {editingId === preset.id ? (
                  <>
                    <Input
                      value={editForm.name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                      className="flex-1 h-8 bg-slate-900 border-slate-700"
                    />
                    {type === 'hole' && (
                      <Input
                        type="number"
                        placeholder="⌀"
                        value={editForm.diameter_mm || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, diameter_mm: parseFloat(e.target.value) || null }))}
                        className="w-16 h-8 bg-slate-900 border-slate-700"
                      />
                    )}
                    <Input
                      type="number"
                      value={editForm.price}
                      onChange={(e) => setEditForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                      className="w-20 h-8 bg-slate-900 border-slate-700"
                    />
                    <Button size="icon" className="h-8 w-8" onClick={saveEdit}>
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditingId(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-slate-300">{preset.name}</span>
                    <span className="font-mono text-amber-500">{preset.price} ₽</span>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEdit(preset)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="h-8 w-8 text-red-500 hover:text-red-400"
                      onClick={() => onDelete(preset.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            ))}
            
            {showAddForm ? (
              <div className="flex items-center gap-2 p-2 bg-slate-950 rounded border border-amber-500/30">
                <Input
                  placeholder="Название"
                  value={newPreset.name}
                  onChange={(e) => setNewPreset(prev => ({ ...prev, name: e.target.value }))}
                  className="flex-1 h-8 bg-slate-900 border-slate-700"
                  data-testid="new-preset-name"
                />
                {type === 'hole' && (
                  <Input
                    type="number"
                    placeholder="⌀мм"
                    value={newPreset.diameter_mm || ''}
                    onChange={(e) => setNewPreset(prev => ({ ...prev, diameter_mm: parseFloat(e.target.value) || null }))}
                    className="w-16 h-8 bg-slate-900 border-slate-700"
                    data-testid="new-preset-diameter"
                  />
                )}
                <Input
                  type="number"
                  placeholder="Цена"
                  value={newPreset.price || ''}
                  onChange={(e) => setNewPreset(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                  className="w-20 h-8 bg-slate-900 border-slate-700"
                  data-testid="new-preset-price"
                />
                <Button size="icon" className="h-8 w-8 bg-amber-500 hover:bg-amber-400" onClick={addPreset} data-testid="save-new-preset">
                  <Check className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setShowAddForm(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                className="w-full border-dashed"
                onClick={() => setShowAddForm(true)}
                data-testid="add-preset-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Добавить пресет
              </Button>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

// ============ MAIN APP ============
function App() {
  const [files, setFiles] = useState([]);
  const [fileData, setFileData] = useState({});
  const [loading, setLoading] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [orderNumberEdited, setOrderNumberEdited] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [companyDetails, setCompanyDetails] = useState({
    company_name: '',
    inn: '',
    address: '',
    phone: '',
    email: '',
    bank_details: '',
    vat_rate: 0,
    director_name: ''
  });
  const [presets, setPresets] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [presetsType, setPresetsType] = useState('cut');

  // Load initial data
  useEffect(() => {
    loadOrderNumber();
    loadCompanyDetails();
    loadPresets();
  }, []);

  const loadOrderNumber = async () => {
    try {
      const res = await axios.get(`${API}/order-number`);
      setOrderNumber(res.data.order_number);
    } catch (e) {
      console.error('Error loading order number:', e);
    }
  };

  const loadCompanyDetails = async () => {
    try {
      const res = await axios.get(`${API}/company-details`);
      setCompanyDetails(res.data);
    } catch (e) {
      console.error('Error loading company details:', e);
    }
  };

  const loadPresets = async () => {
    try {
      const res = await axios.get(`${API}/material-presets`);
      setPresets(res.data);
    } catch (e) {
      console.error('Error loading presets:', e);
    }
  };

  const saveCompanyDetails = async (details) => {
    try {
      await axios.post(`${API}/company-details`, details);
      setCompanyDetails(details);
      toast.success('Реквизиты сохранены');
    } catch (e) {
      toast.error('Ошибка сохранения реквизитов');
    }
  };

  const savePreset = async (id, data) => {
    try {
      if (id) {
        await axios.put(`${API}/material-presets/${id}`, data);
      } else {
        await axios.post(`${API}/material-presets`, data);
      }
      await loadPresets();
      toast.success('Пресет сохранён');
    } catch (e) {
      toast.error('Ошибка сохранения пресета');
    }
  };

  const deletePreset = async (id) => {
    try {
      await axios.delete(`${API}/material-presets/${id}`);
      await loadPresets();
      toast.success('Пресет удалён');
    } catch (e) {
      toast.error('Ошибка удаления пресета');
    }
  };

  // File upload handler
  const onDrop = useCallback(async (acceptedFiles) => {
    setLoading(true);
    
    for (const file of acceptedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const res = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setFiles(prev => [...prev, res.data]);
        toast.success(`Файл ${file.name} загружен`);
      } catch (e) {
        const errorMsg = e.response?.data?.detail || 'Ошибка загрузки файла';
        toast.error(errorMsg);
      }
    }
    
    setLoading(false);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/svg+xml': ['.svg'],
      'application/dxf': ['.dxf'],
      'application/pdf': ['.pdf'],
      'application/postscript': ['.ai'],
      'application/acad': ['.dwg']
    }
  });

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setFileData(prev => {
      const newData = { ...prev };
      delete newData[index];
      return newData;
    });
  };

  const updateFileData = useCallback((index, data) => {
    setFileData(prev => ({ ...prev, [index]: data }));
  }, []);

  const openPresets = (type) => {
    setPresetsType(type);
    setShowPresets(true);
  };

  // Calculate grand total
  const grandTotal = Object.values(fileData).reduce((sum, data) => sum + (data?.fileTotal || 0), 0);
  const vatAmount = companyDetails.vat_rate > 0 ? Math.round(grandTotal * companyDetails.vat_rate / 100) : 0;
  const grandTotalWithVat = grandTotal + vatAmount;

  // PDF Export
  const exportPDF = async () => {
    // Dynamically import jspdf and font
    const { jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    const { DejaVuSansBase64, DejaVuSansBoldBase64 } = await import('./fonts/DejaVuSans.js');
    
    const doc = new jsPDF();
    
    // Add DejaVu Sans fonts with Cyrillic support
    doc.addFileToVFS('DejaVuSans-normal.ttf', DejaVuSansBase64);
    doc.addFont('DejaVuSans-normal.ttf', 'DejaVuSans', 'normal');
    doc.addFileToVFS('DejaVuSans-bold.ttf', DejaVuSansBoldBase64);
    doc.addFont('DejaVuSans-bold.ttf', 'DejaVuSans', 'bold');
    doc.setFont('DejaVuSans');
    
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header bar
    doc.setFillColor(72, 71, 67);
    doc.rect(0, 0, pageWidth, 20, 'F');
    
    // Logo text
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.text('FREZO.PRO', 10, 13);
    
    // Order number and date
    doc.setFontSize(10);
    const currentDate = new Date().toLocaleDateString('ru-RU');
    doc.text('Счёт № ' + orderNumber, pageWidth - 60, 10);
    doc.text('от ' + currentDate, pageWidth - 60, 16);
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    
    let yPos = 30;
    
    // Company details
    if (companyDetails.company_name) {
      doc.setFontSize(10);
      doc.text('Исполнитель:', 10, yPos);
      yPos += 6;
      doc.setFontSize(9);
      doc.text(companyDetails.company_name, 10, yPos);
      yPos += 5;
      if (companyDetails.inn) {
        doc.text('ИНН: ' + companyDetails.inn, 10, yPos);
        yPos += 5;
      }
      if (companyDetails.address) {
        doc.text(companyDetails.address, 10, yPos);
        yPos += 5;
      }
      if (companyDetails.phone) {
        doc.text('Тел: ' + companyDetails.phone, 10, yPos);
        yPos += 5;
      }
      if (companyDetails.email) {
        doc.text('Email: ' + companyDetails.email, 10, yPos);
        yPos += 5;
      }
      if (companyDetails.bank_details) {
        const bankLines = companyDetails.bank_details.split('\n');
        bankLines.forEach(line => {
          doc.text(line, 10, yPos);
          yPos += 4;
        });
      }
      yPos += 5;
    }
    
    // Customer
    if (customerName) {
      doc.setFontSize(10);
      doc.text('Заказчик:', 10, yPos);
      yPos += 6;
      doc.setFontSize(9);
      doc.text(customerName, 10, yPos);
      yPos += 10;
    }
    
    // Files tables
    files.forEach((file, index) => {
      const data = fileData[index] || {};
      
      doc.setFontSize(10);
      doc.text(file.filename, 10, yPos);
      yPos += 2;
      
      const tableData = [];
      
      // Cut row - renamed to ЧПУ Фрезеровка
      tableData.push([
        'ЧПУ Фрезеровка',
        (data.cutLength || file.cut_length_meters) + ' м',
        (data.cutPrice || 100) + ' руб/м',
        String(data.quantity || 1),
        (data.cutTotal || 0) + ' руб'
      ]);
      
      // Holes rows
      file.holes.forEach(hole => {
        const holePrice = data.holePrices?.[hole.diameter_mm] || 20;
        const holeTotal = Math.round((hole.count * holePrice * (data.quantity || 1)) / 10) * 10;
        tableData.push([
          'Отверстие d' + hole.diameter_mm + 'мм',
          hole.count + ' шт',
          holePrice + ' руб/шт',
          String(data.quantity || 1),
          holeTotal + ' руб'
        ]);
      });
      
      // Sampling row - ЧПУ Выборка
      if (data.sampling > 0) {
        tableData.push([
          'ЧПУ Выборка',
          '1',
          (data.sampling || 0) + ' руб',
          String(data.quantity || 1),
          (data.samplingTotal || 0) + ' руб'
        ]);
      }
      
      autoTable(doc, {
        startY: yPos,
        head: [['Наименование', 'Количество', 'Цена', 'Тираж', 'Сумма']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [72, 71, 67], font: 'DejaVuSans' },
        bodyStyles: { font: 'DejaVuSans' },
        styles: { fontSize: 9, font: 'DejaVuSans' }
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
    });
    
    // Grand total with VAT
    doc.setFillColor(245, 158, 11);
    doc.rect(pageWidth - 80, yPos, 70, 10, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.text('ИТОГО: ' + grandTotalWithVat.toLocaleString('ru-RU') + ' руб', pageWidth - 75, yPos + 7);
    yPos += 15;
    
    // VAT info - show only "в том числе НДС"
    if (companyDetails.vat_rate > 0) {
      doc.setFontSize(9);
      doc.text('В том числе НДС ' + companyDetails.vat_rate + '%: ' + vatAmount.toLocaleString('ru-RU') + ' руб', pageWidth - 80, yPos);
      yPos += 10;
    }
    
    // Director signature
    if (companyDetails.director_name) {
      yPos += 10;
      doc.setFontSize(9);
      doc.text('Руководитель организации:', 10, yPos);
      yPos += 10;
      doc.line(10, yPos, 80, yPos);
      doc.text('/ ' + companyDetails.director_name + ' /', 85, yPos);
      yPos += 5;
      doc.setFontSize(8);
      doc.text('(подпись)', 35, yPos);
    }
    
    // Save PDF
    doc.save('Счёт_' + orderNumber.replace('/', '-') + '.pdf');
    
    // Increment order number
    if (!orderNumberEdited) {
      try {
        const res = await axios.post(`${API}/order-number/increment`);
        setOrderNumber(res.data.order_number);
      } catch (e) {
        console.error('Error incrementing order number:', e);
      }
    }
    
    toast.success('PDF счёт сохранён');
  };

  const cutPresets = presets.filter(p => p.type === 'cut');
  const holePresets = presets.filter(p => p.type === 'hole');

  return (
    <div className="min-h-screen bg-slate-950">
      <Toaster richColors position="top-right" />
      
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/assets/logo.jpg" 
              alt="Frezo.pro" 
              className="h-10 w-auto rounded"
            />
            <div>
              <h1 className="font-manrope font-bold text-white text-lg tracking-tight">
                QUOTE.FREZO
              </h1>
              <p className="text-xs text-slate-500">Калькулятор стоимости ЧПУ фрезеровки</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500">Счёт №</Label>
              <Input
                value={orderNumber}
                onChange={(e) => {
                  setOrderNumber(e.target.value);
                  setOrderNumberEdited(true);
                }}
                className="w-32 h-8 bg-slate-950 border-slate-800 font-mono text-amber-500"
                data-testid="order-number-input"
              />
            </div>
            
            <Button
              variant="secondary"
              size="icon"
              onClick={() => setShowSettings(true)}
              data-testid="settings-btn"
            >
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Customer Name */}
        <div className="flex items-center gap-3 mb-6">
          <Label className="text-xs font-manrope uppercase tracking-widest text-slate-500">
            Заказчик
          </Label>
          <Input
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="Введите имя заказчика"
            className="max-w-md bg-slate-900 border-slate-800"
            data-testid="customer-name-input"
          />
        </div>

        {/* Dropzone */}
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-sm p-8 mb-6 text-center cursor-pointer
            transition-colors duration-200
            ${isDragActive 
              ? 'border-amber-500 bg-amber-500/10' 
              : 'border-slate-700 hover:border-slate-600 bg-slate-900/50'
            }
          `}
          data-testid="file-dropzone"
        >
          <input {...getInputProps()} />
          <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragActive ? 'text-amber-500' : 'text-slate-600'}`} />
          <p className="font-manrope text-slate-400 mb-2">
            {isDragActive ? 'Отпустите файлы здесь' : 'Перетащите файлы сюда или нажмите для выбора'}
          </p>
          <p className="text-xs text-slate-600">
            Поддерживаемые форматы: SVG, DXF, PDF, AI, DWG
          </p>
        </div>

        {/* Loading indicator */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
            <span className="ml-3 text-slate-400">Обработка файла...</span>
          </div>
        )}

        {/* File Cards */}
        <div className="space-y-4 mb-6">
          {files.map((file, index) => (
            <FileCard
              key={index}
              file={file}
              index={index}
              onRemove={removeFile}
              onUpdate={updateFileData}
              cutPresets={cutPresets}
              holePresets={holePresets}
              onOpenPresets={openPresets}
            />
          ))}
        </div>

        {/* Grand Total & Export */}
        {files.length > 0 && (
          <div className="bg-slate-900 border border-slate-800 rounded-sm p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <span className="font-manrope text-slate-400 text-lg">Общий итог:</span>
                <span className="font-mono text-3xl text-amber-500 font-bold ml-4" data-testid="grand-total">
                  {grandTotal.toLocaleString('ru-RU')} ₽
                </span>
                {companyDetails.vat_rate > 0 && (
                  <div className="text-sm text-slate-500 mt-1">
                    В т.ч. НДС {companyDetails.vat_rate}%: {vatAmount.toLocaleString('ru-RU')} ₽
                    <br />
                    Всего с НДС: <span className="text-amber-400">{grandTotalWithVat.toLocaleString('ru-RU')} ₽</span>
                  </div>
                )}
              </div>
              
              <Button
                onClick={exportPDF}
                className="bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-wide"
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Скачать PDF
              </Button>
            </div>
          </div>
        )}

        {/* Empty state */}
        {files.length === 0 && !loading && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto text-slate-700 mb-4" />
            <h3 className="font-manrope text-slate-500 text-lg mb-2">Нет загруженных файлов</h3>
            <p className="text-slate-600 text-sm">
              Загрузите файлы чертежей для расчёта стоимости
            </p>
          </div>
        )}
      </main>

      {/* Dialogs */}
      <CompanySettingsDialog
        open={showSettings}
        onOpenChange={setShowSettings}
        companyDetails={companyDetails}
        onSave={saveCompanyDetails}
      />

      <PresetsDialog
        open={showPresets}
        onOpenChange={setShowPresets}
        presets={presets}
        onSave={savePreset}
        onDelete={deletePreset}
        type={presetsType}
      />
    </div>
  );
}

export default App;
